import React from 'react';
import { Link } from '@material-ui/core';
import { EmptyState, Progress, Table, WarningPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { useEntity } from '@backstage/plugin-catalog-react';

const DEFAULT_DEPLOY_WORKFLOW_FILES = [
  'build-release.yaml',
  'build-docker.yaml',
  'gitops.yaml',
  'migrate-qa-db.yaml',
  'migrate-prod-db.yaml',
];
const WORKFLOW_FILES_ANNOTATION = 'incentive.me/github-actions-workflows';

type Workflow = {
  id: number;
  name: string;
  path: string;
};

type WorkflowRun = {
  id: number;
  name: string;
  html_url: string;
  status: string;
  conclusion: string | null;
  updated_at: string;
  head_branch: string;
  workflow_id: number;
  display_title?: string;
};

type WorkflowRunRow = {
  workflow: string;
  title: string;
  url: string;
  status: string;
  conclusion: string;
  branch: string;
  updatedAt: string;
};

const normalizeWorkflowFiles = (
  value?: string | string[] | null,
): string[] | null => {
  if (!value) {
    return null;
  }

  const entries = Array.isArray(value)
    ? value.map(item => item.trim())
    : value
        .split(',')
        .map(item => item.trim());

  const files = entries.filter(Boolean);
  return files.length ? files : null;
};

const toWorkflowPath = (file: string): string => {
  if (file.startsWith('.github/workflows/')) {
    return file;
  }
  return `.github/workflows/${file}`;
};

export const EntityGithubActionsDeployContent = () => {
  const { entity } = useEntity();
  const scmAuthApi = useApi(scmAuthApiRef);

  const projectSlug =
    entity.metadata.annotations?.['github.com/project-slug'];
  const workflowFiles = React.useMemo(() => {
    const annotationValue =
      entity.metadata.annotations?.[WORKFLOW_FILES_ANNOTATION];
    const annotationFiles = normalizeWorkflowFiles(annotationValue);
    return annotationFiles ?? DEFAULT_DEPLOY_WORKFLOW_FILES;
  }, [entity.metadata.annotations]);

  const [state, setState] = React.useState<{
    loading: boolean;
    error?: Error;
    rows: WorkflowRunRow[];
  }>({ loading: true, rows: [] });

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!projectSlug) {
        setState({ loading: false, rows: [] });
        return;
      }

      const [owner, repo] = projectSlug.split('/');
      if (!owner || !repo) {
        setState({
          loading: false,
          rows: [],
          error: new Error(`Invalid github.com/project-slug: ${projectSlug}`),
        });
        return;
      }

      try {
        const { token } = await scmAuthApi.getCredentials({
          url: `https://github.com/${owner}/${repo}`,
        });

        const headers = token
          ? { Authorization: `token ${token}` }
          : undefined;

        const fetchJson = async (url: string) => {
          const response = await fetch(url, { headers });
          if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
          }
          return response.json();
        };

        const workflowList = await fetchJson(
          `https://api.github.com/repos/${owner}/${repo}/actions/workflows`,
        );

        const workflows: Workflow[] = workflowList.workflows ?? [];
        const allowedPaths = workflowFiles
          ? new Set(workflowFiles.map(file => toWorkflowPath(file)))
          : null;
        const allowedWorkflows = allowedPaths
          ? workflows.filter(workflow => allowedPaths.has(workflow.path))
          : workflows;

        if (!allowedWorkflows.length) {
          if (!cancelled) {
            setState({ loading: false, rows: [] });
          }
          return;
        }

        const runs: WorkflowRun[] = [];
        for (const workflow of allowedWorkflows) {
          const runsResponse = await fetchJson(
            `https://api.github.com/repos/${owner}/${repo}/actions/workflows/${workflow.id}/runs?per_page=20`,
          );
          const workflowRuns: WorkflowRun[] = runsResponse.workflow_runs ?? [];
          for (const run of workflowRuns) {
            runs.push(run);
          }
        }

        const workflowById = new Map(
          allowedWorkflows.map(workflow => [workflow.id, workflow.name]),
        );

        const rows: WorkflowRunRow[] = runs
          .map(run => ({
            workflow: workflowById.get(run.workflow_id) || run.name,
            title: run.display_title || run.name,
            url: run.html_url,
            status: run.status,
            conclusion: run.conclusion || '-',
            branch: run.head_branch,
            updatedAt: run.updated_at,
          }))
          .sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() -
              new Date(a.updatedAt).getTime(),
          );

        if (!cancelled) {
          setState({ loading: false, rows });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            rows: [],
            error: error as Error,
          });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [projectSlug, scmAuthApi, workflowFiles]);

  if (!projectSlug) {
    return (
      <EmptyState
        title="No GitHub repository configured"
        missing="info"
        description="Add the github.com/project-slug annotation to enable GitHub Actions data."
      />
    );
  }

  if (state.loading) {
    return <Progress />;
  }

  if (state.error) {
    return (
      <WarningPanel title="Failed to load GitHub Actions runs">
        {state.error.message}
      </WarningPanel>
    );
  }

  if (!state.rows.length) {
    return (
      <EmptyState
        title={
          workflowFiles
            ? 'No deploy workflows found'
            : 'No workflow runs found'
        }
        missing="data"
        description={
          workflowFiles
            ? `Expected workflows: ${workflowFiles.join(', ')}`
            : 'No workflow runs were returned for this repository.'
        }
      />
    );
  }

  return (
    <Table
      title="Deploy workflows"
      options={{ paging: true, search: false, pageSize: 20 }}
      columns={[
        { title: 'Workflow', field: 'workflow' },
        {
          title: 'Run',
          field: 'title',
          render: row => (
            <Link
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.title}
            </Link>
          ),
        },
        { title: 'Status', field: 'status' },
        { title: 'Conclusion', field: 'conclusion' },
        { title: 'Branch', field: 'branch' },
        {
          title: 'Updated',
          field: 'updatedAt',
          render: row => new Date(row.updatedAt).toLocaleString(),
        },
      ]}
      data={state.rows}
    />
  );
};
